import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { BlockFormSelectOrderCard } from '@eika-kortbetjening/block-form-select-order-card';

import SkeletonList from '@/components/skeleton/SkeletonList';
import piwik from '@/piwik/piwik';
import { useCard } from '@/react-query/hooks/query/use-card';
import { navigateToCardDetails } from '@/utils/url/get-card-details-url-utils';
import { getRootUrl, navigateToHardBlockConfirm, navigateToPinChangeDecision } from '@/utils/url/get-url-utils';

import { useLinkProducer, useNavigate } from '@domain/app';
import { HardBlockReason } from '@generated-types';
import useReasonQuery from '@hooks/query/use-reason-query';
import useBankAllowedOperations from '@hooks/use-bank-allowed-operations';
import { useIsHighFraudRisk } from '@hooks/use-is-high-risk';
import useText from '@hooks/use-text';
import { canReorder, canSetPin } from '@utils/allowed-operations/card-allowed-operations-utils';

const SelectReorder = () => {
    const { data: card, isLoading } = useCard();
    const [selectedReorder, setSelectedReorder] = useState<boolean | undefined>(undefined);
    const [selectedReorderError, setSelectedReorderError] = useState<string | undefined>(undefined);
    const text = useText();
    const reasonQuery = useReasonQuery();
    const { canReorderCard: bankAllowsReorder } = useBankAllowedOperations();
    const isHighFraudRisk = useIsHighFraudRisk();
    const navigate = useNavigate();
    const location = useLocation();
    const linkProducer = useLinkProducer();

    if (typeof card === 'undefined' || isLoading) {
        return <SkeletonList />;
    }

    if (reasonQuery === null) {
        return <Navigate to={getRootUrl(linkProducer)} replace />;
    }

    if (isHighFraudRisk || !bankAllowsReorder || !canReorder(card) || card.allowedOperations.migrationWeekStart) {
        navigateToHardBlockConfirm(navigate)({ card, selectedReason: reasonQuery, reorder: false });
    }

    const next = () => {
        if (typeof selectedReorder === 'undefined') {
            setSelectedReorderError(text.messageErrorOptionRequired);
            return;
        }

        if (!selectedReorder) {
            navigateToHardBlockConfirm(navigate)({ card, selectedReason: reasonQuery, reorder: false });
            return;
        }

        if (canSetPin(card)) {
            if (reasonQuery === HardBlockReason.STOLEN || reasonQuery === HardBlockReason.LOST) {
                navigateToPinChangeDecision(navigate, location);
                return;
            }
        }

        navigateToHardBlockConfirm(navigate)({ card, selectedReason: reasonQuery, reorder: selectedReorder });
    };

    const cancel = () => {
        navigateToCardDetails(navigate)(card);
    };

    return (
        <BlockFormSelectOrderCard
            selectedReorderError={selectedReorderError}
            next={next}
            cancel={cancel}
            reorderInfo={{ selectedReorder, setSelectedReorder }}
            sitecoreDictionary={text}
            piwik={piwik}
            userIsHighFraudRisk={isHighFraudRisk}
        />
    );
};

export default SelectReorder;
